import { maybe } from '@passionware/monads';
import { createSimpleEvent } from '@passionware/simple-event';
import { createSimpleStore, useSimpleStore } from '@passionware/simple-store';
import { QueryClient, useQuery } from '@tanstack/react-query';
import {
  GlobalSearchApi,
  GlobalSearchEntry
} from '../../api/global-search/global-search.api';
import {
  GlobalSearchRecentsByScope,
  globalSearchRecentsScopeKey
} from '../../api/global-search/global-search.api.ls.schema';
import { ensureIdleQuery } from '../../platform/ensureIdleQuery';
import { ReadWriteApi } from '../../platform/ts/readWriteApi';
import { GlobalSearchService } from './GlobalSearchService';
import { WithErrorService } from '../front/ErrorService/ErrorService';
import { WithServices } from '../../platform/ts/services';

export function createGlobalSearchService(
  config: WithServices<[WithErrorService]> & {
    api: GlobalSearchApi;
    recentsApi: ReadWriteApi<GlobalSearchRecentsByScope>;
    queryClient: QueryClient;
    maxRecentSearches?: number;
  }
): GlobalSearchService {
  const {
    api,
    recentsApi,
    queryClient,
    maxRecentSearches = 5,
    services: { errorService }
  } = config;
  const interceptor = errorService.withCategory('GlobalSearchService');
  const openEvent = createSimpleEvent();
  const recentsByScope = createSimpleStore<GlobalSearchRecentsByScope>({});
  async function boot() {
    const recentSearches = await recentsApi.load();
    recentsByScope.setNewValue(recentSearches);
  }

  boot();
  return {
    useSearch: (query, enterpriseWorkspaceId) => {
      return ensureIdleQuery(
        query,
        useQuery(
          {
            enabled: maybe.isPresent(query),
            queryKey: [
              'global-search',
              query,
              maybe.getOrElse(enterpriseWorkspaceId, null)
            ],
            queryFn: interceptor.wrap(context =>
              api.search(
                {
                  query: query!,
                  enterpriseWorkspaceId: maybe.getOrElse(
                    enterpriseWorkspaceId,
                    null
                  )
                },
                context.signal
              )
            )
          },
          queryClient
        )
      );
    },
    storeRecentSearchItem: (item, enterpriseWorkspaceId) => {
      const scopeKey = globalSearchRecentsScopeKey(
        maybe.getOrElse(enterpriseWorkspaceId, null)
      );
      recentsByScope.setNewValue(prev => {
        const bucket = prev[scopeKey] ?? [];
        const nextBucket = [
          item,
          ...bucket.filter(i => !(i.id === item.id && i.group === item.group))
        ].slice(0, maxRecentSearches);
        const next = { ...prev, [scopeKey]: nextBucket };
        recentsApi.save(next);
        return next;
      });
    },
    useRecentSearches: enterpriseWorkspaceId => {
      const all = useSimpleStore(recentsByScope);
      const scopeKey = globalSearchRecentsScopeKey(
        maybe.getOrElse(enterpriseWorkspaceId, null)
      );
      return all[scopeKey] ?? [];
    },
    openSearchEvent: openEvent
  };
}
